import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
// Icons importés séparément → tree-shaking optimal
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import UserProfile from '../../UserProfile';
import env from 'common/env';
import { version } from '../../../package.json';

// Schéma de validation défini hors du composant → pas recréé à chaque render
const schema = {
    login: {
        presence: { allowEmpty: false, message: 'est requis' },
        email: false,
        length: { maximum: 64 }
    },
    password: {
        presence: { allowEmpty: false, message: 'est requis' },
        length: { maximum: 128 }
    }
};

// État initial sorti du composant → référence stable, pas recréée
const INITIAL_FORM_STATE = {
    isValid: false,
    values: {},
    touched: {},
    errors: {}
};

const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
    },

    // Fond : will-change pour indiquer au navigateur l'animation à venir
    background: {
        position: 'fixed',
        inset: 0,
        backgroundImage: 'url(/images/auth.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: 0,
        willChange: 'filter, transform',
        animation: '$bgBlurIn 2.5s cubic-bezier(0.22, 1, 0.36, 1) both'
    },

    '@keyframes bgBlurIn': {
        from: { filter: 'blur(18px) brightness(0.6)', transform: 'scale(1.06)' },
        to: { filter: 'blur(0px) brightness(1)', transform: 'scale(1)' }
    },

    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(160deg, rgba(8,18,12,0.78) 0%, rgba(20,45,28,0.65) 100%)',
        zIndex: 1
    },

    card: {
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: 420,
        padding: theme.spacing(5, 4.5),
        paddingBottom: 20,
        background: '#fff',
        willChange: 'transform, opacity',
        animation: '$fadeUp 2s cubic-bezier(0.22, 1, 0.36, 1) both',
        borderRadius: 8,
        backgroundColor: 'rgba(255,255,255,0.97)',
        boxShadow: '0 40px 100px rgba(0,0,0,0.45), 0 4px 16px rgba(0,0,0,0.15)',

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(4, 3),
            margin: theme.spacing(2, 1.5),
            maxWidth: '100%',
            paddingBottom: 20
        }
    },

    '@keyframes fadeUp': {
        from: { opacity: 1, transform: 'translateY(24px)' },
        to: { opacity: 1, transform: 'translateY(0)' }
    },

    logoWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing(3.5)
    },
    logo: {
        height: 35,
        width: 'auto',
        objectFit: 'contain',
        [theme.breakpoints.down('xs')]: {
            height: 30
        }
    },
    logoFallback: {
        display: 'none',
        fontWeight: 700,
        fontSize: '1.4rem',
        color: '#163d25',
        letterSpacing: '-0.01em'
    },

    textField: {
        marginBottom: theme.spacing(2)
    },

    cardFooter: {
        paddingTop: theme.spacing(2),
        borderTop: '1px solid #e5e7eb',
        textAlign: 'center'
    },
    footerText: {
        fontSize: '0.7rem',
        color: '#9ca3af',
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
    },

    tagline: {
        position: 'fixed',
        bottom: theme.spacing(4),
        zIndex: 2,
        textAlign: 'center'
    },
    taglineText: {
        fontSize: '0.9375rem',
        color: 'rgba(255,255,255,0.65)'
    },
    taglineSub: {
        fontSize: '0.7rem',
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        marginTop: 4
    }
}));

const SignIn = ({ history }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formState, setFormState] = useState(INITIAL_FORM_STATE);

    // useEffect déclenché uniquement sur les valeurs, pas le formState entier
    useEffect(() => {
        const errors = validate(formState.values, schema);
        setFormState(fs => ({
            ...fs,
            isValid: !errors,
            errors: errors || {}
        }));
    }, [formState.values]);

    // useCallback → fonction stable entre les renders, évite les re-renders des champs
    const handleChange = useCallback(event => {
        const { name, type, value, checked } = event.target;
        setFormState(fs => ({
            ...fs,
            values: {
                ...fs.values,
                [name]: type === 'checkbox' ? checked : value
            },
            touched: { ...fs.touched, [name]: true }
        }));
    }, []);

    const handleSignIn = useCallback(
        event => {
            event.preventDefault();
            if (!formState.isValid || isLoading) return;
            setIsLoading(true);

            const login = formState.values.login.toLowerCase();
            const mdp = formState.values.password.toLowerCase();

            fetch(`${env}/user/sign-in?username=${login}&mdp=${mdp}`)
                .then(res => res.json())
                .then(data => {
                    if (data) {
                        UserProfile.setId(data.id);
                        UserProfile.setName(data.username);
                        UserProfile.setProfile(data.profile);
                        UserProfile.setDomaine(data.domaine);
                        UserProfile.setRoleId(data.roleId);
                        UserProfile.setToken(data.token);
                        UserProfile.setContributor(data.contributor);
                        UserProfile.setConfig(JSON.stringify(data.config));
                        history.push('/home');
                    } else {
                        alert('Login ou mot de passe invalide');
                    }
                })
                .catch(() => alert('Login ou mot de passe invalide'))
                .finally(() => setIsLoading(false));
        },
        [formState.isValid, formState.values, isLoading, history]
    );

    const hasError = useCallback(field => !!(formState.touched[field] && formState.errors[field]), [
        formState.touched,
        formState.errors
    ]);

    const handleLogoError = useCallback(e => {
        e.target.style.display = 'none';
        if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
    }, []);

    const togglePassword = useCallback(() => setShowPassword(v => !v), []);

    return (
        <div className={classes.root}>
            <div className={classes.background} />
            <div className={classes.overlay} />

            <div className={classes.card}>
                <div className={classes.logoWrapper}>
                    {/* fetchpriority="high" : le logo est chargé en priorité */}
                    <img
                        src="/images/logos/logo-digitmarche.png"
                        alt="Digit'Marché"
                        className={classes.logo}
                        onError={handleLogoError}
                        fetchpriority="high"
                        width="auto"
                        height="35"
                    />
                    <span className={classes.logoFallback}>Digit'Marché</span>
                </div>

                <form onSubmit={handleSignIn} noValidate>
                    <TextField
                        className={classes.textField}
                        error={hasError('login')}
                        fullWidth
                        helperText={hasError('login') ? formState.errors.login[0] : null}
                        label="Nom d'utilisateur"
                        name="login"
                        onChange={handleChange}
                        type="text"
                        value={formState.values.login || ''}
                        variant="outlined"
                        autoComplete="username"
                        autoFocus={!isMobile}
                    />
                    <TextField
                        className={classes.textField}
                        error={hasError('password')}
                        fullWidth
                        helperText={hasError('password') ? formState.errors.password[0] : null}
                        label="Mot de passe"
                        name="password"
                        onChange={handleChange}
                        type={showPassword ? 'text' : 'password'}
                        value={formState.values.password || ''}
                        variant="outlined"
                        autoComplete="current-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={togglePassword}
                                        edge="end"
                                        aria-label={
                                            showPassword
                                                ? 'Masquer le mot de passe'
                                                : 'Afficher le mot de passe'
                                        }>
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        disabled={!formState.isValid || isLoading}
                        fullWidth
                        size="large"
                        color="primary"
                        type="submit"
                        variant="contained"
                        disableElevation>
                        {isLoading ? <CircularProgress size={22} /> : 'Connexion'}
                    </Button>
                </form>

                <div className={classes.cardFooter}>
                    <Typography className={classes.footerText}>DigitMarché {version}</Typography>
                </div>
            </div>

            <div className={classes.tagline}>
                <Typography className={classes.taglineText}>Digitalisons nos marchés</Typography>
                <Typography className={classes.taglineSub}>
                    DigitMarché &copy; {new Date().getFullYear()}
                </Typography>
            </div>
        </div>
    );
};

SignIn.propTypes = {
    history: PropTypes.object
};

export default SignIn;
